import { Card, Center, Circle, Container, Icon, Stack } from '@chakra-ui/react'
import type { FC } from 'react'
import { useEffect, useState } from 'react'
import { BsStars } from 'react-icons/bs'
import { useLoaderData, useNavigate } from 'react-router-dom'

import TasksFetcher from '@app/fetchers/tasksFetcher'
import useGetObjectsByProperties from '@app/hooks/useGetObjectsByProperties'
import WelcomeBusinessContext from '@app/pages/onboarding/components/welcomeBusinessContext'
import WelcomeDescription from '@app/pages/onboarding/components/welcomeDescription'
import WelcomeFooter from '@app/pages/onboarding/components/welcomeFooter'
import WelcomeProgressBar from '@app/pages/onboarding/components/welcomeProgressBar'
import AiPlaybooksModal from '@app/pages/playbooks/aiPlaybooksModal'
import type { PlaybooksQuery } from '@graphql/queries'
import { useBusinessContextQuery } from '@graphql/queries'
import { TaskTypesEnum } from '@graphql/types'

const titleSteps = [
  'Initializing analysis...',
  'Analyzing business category...',
  'Analyzing business description...',
  'Analyzing business model...',
  'Evaluating competitors...',
  'Analyzing geographic presence...',
  'Extracting important metrics...',
  'Identifying critical work areas...',
  'Analyzing language style...',
  'Identifying notable customers...',
  'Analyzing pricing structure...',
  'Analyzing products and services...',
  'Synthesizing complete business context...',
  'Your business context is ready.'
]

const STEP_TIMEOUT = 3000 // 3 seconds in milliseconds

const Welcome: FC = () => {
  const navigate = useNavigate()
  const { playbooks } = useLoaderData() as { playbooks: PlaybooksQuery['playbooks'] }

  const [loading, setLoading] = useState(false)
  const [titleText, setTitleText] = useState(titleSteps[0])
  const [titleIndex, setTitleIndex] = useState(0)
  const [businessContext, setBusinessContext] = useState(null)

  const tasks = useGetObjectsByProperties('task', { taskType: TaskTypesEnum.RootContextCreate })
  const taskCompleted = tasks.find((task) => task.status === 'completed')
  const taskFailed = tasks.find((task) => task.status === 'failed')

  const [{ data }] = useBusinessContextQuery({
    pause: !tasks.length || !taskCompleted || businessContext,
    variables: { id: null }
  })

  // Move between the steps every 3 seconds until the last step. If the task is completed, show the last step.
  useEffect(() => {
    if (taskCompleted) {
      setTitleText(titleSteps[titleSteps.length - 1])
      setTitleIndex(titleSteps.length - 1)
    } else if (titleIndex < titleSteps.length - 2) {
      setTimeout(() => {
        const newIndex = titleIndex + 1

        setTitleText(titleSteps[newIndex])
        setTitleIndex(newIndex)
      }, STEP_TIMEOUT)
    }
  }, [taskCompleted, titleIndex])

  // If we got data from the query, set the business context
  useEffect(() => {
    if (data && !businessContext) {
      setTitleText(titleSteps[titleSteps.length - 1])
      setTitleIndex(titleSteps.length - 1)

      setBusinessContext(data?.businessContext || {})
    }
  }, [businessContext, data])

  // If the Task failed, navigate to the Playbooks page
  useEffect(() => {
    if (taskFailed) {
      navigate('/maps')
    }
  }, [navigate, taskFailed])

  return (
    <>
      <TasksFetcher loading={loading} setLoading={setLoading} />
      <Center p={8}>
        <Container maxW="2xl">
          <Card.Root>
            <Card.Header>
              <Stack gap={4} align="center">
                <Circle bg="purple.500" p={3}>
                  <Icon boxSize={12} color="white">
                    <BsStars />
                  </Icon>
                </Circle>
                <Card.Title>{titleText}</Card.Title>
              </Stack>
            </Card.Header>
            <Card.Body>
              <Stack gap={6}>
                <AiPlaybooksModal
                  showCTA={!!businessContext}
                  playbooks={playbooks}
                  businessContextId={businessContext?.id}
                />
                <WelcomeProgressBar
                  stepCount={titleSteps.length}
                  currentStep={titleIndex}
                  showProgressBar={!businessContext}
                />
                <WelcomeDescription showDescription={!businessContext} />
                <WelcomeBusinessContext businessContext={businessContext} />
              </Stack>
            </Card.Body>
            <Card.Footer>
              <WelcomeFooter showFooter={true} disabled={!businessContext} />
            </Card.Footer>
          </Card.Root>
        </Container>
      </Center>
    </>
  )
}

export default Welcome
